import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { calculateOptionValueFromAPI, calculateGreeks, calculateTimeToMaturity } from '../../services/optionPricing';
import './OptionStrategies.css';

// =====================================================
// توابع کمکی برای تحلیل تکنیکال نماد پایه
// =====================================================

// تابع برای تخمین نزدیکی به earnings
// در بازار ایران، earnings معمولاً هر 3 ماه یکبار است
function estimateEarningsProximity(baseSymbol) {
  // این یک تخمین ساده است
  // در واقعیت باید از داده‌های واقعی استفاده شود

  // بر اساس حروف نماد، یک روز تخمینی تولید می‌کنیم
  // این فقط برای نمایش است و نباید به عنوان داده واقعی استفاده شود
  const hash = baseSymbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const daysUntilEarnings = (hash % 90) + 1; // بین 1 تا 90 روز

  return {
    daysUntilEarnings,
    text: getEarningsText(daysUntilEarnings),
    color: getEarningsColor(daysUntilEarnings),
    level: getEarningsLevel(daysUntilEarnings)
  };
}

function getEarningsText(days) {
  if (days <= 7) return 'خیلی نزدیک';
  if (days <= 14) return 'نزدیک';
  if (days <= 30) return 'متوسط';
  if (days <= 60) return 'دور';
  return 'خیلی دور';
}

function getEarningsColor(days) {
  if (days <= 7) return '#f44336'; // قرمز - خیلی نزدیک
  if (days <= 14) return '#ff9800'; // نارنجی - نزدیک
  if (days <= 30) return '#ffeb3b'; // زرد - متوسط
  if (days <= 60) return '#8bc34a'; // سبز روشن - دور
  return '#4caf50'; // سبز - خیلی دور
}

function getEarningsLevel(days) {
  if (days <= 7) return 5;
  if (days <= 14) return 4;
  if (days <= 30) return 3;
  if (days <= 60) return 2;
  return 1;
}

// تابع برای محاسبه IV Crush Risk (ریسک افت IV بعد از earnings)
function calculateIVCrushRisk(daysUntilEarnings, strategyType) {
  if (daysUntilEarnings === null) return { text: '-', color: '#9e9e9e', risk: 0 };

  let riskLevel = 'پایین';
  let riskColor = '#4caf50';
  let risk = 1;

  if (daysUntilEarnings <= 7) {
    riskLevel = 'بسیار بالا';
    riskColor = '#c62828';
    risk = 5;
  } else if (daysUntilEarnings <= 14) {
    riskLevel = 'بالا';
    riskColor = '#f44336';
    risk = 4;
  } else if (daysUntilEarnings <= 30) {
    riskLevel = 'متوسط';
    riskColor = '#ff9800';
    risk = 3;
  }

  // برای استراتژی‌های فروش، ریسک کمتر است
  if (strategyType.includes('sell') || strategyType.includes('covered') || strategyType.includes('cash')) {
    risk = Math.max(1, risk - 2);
    if (risk <= 1) {
      riskLevel = 'پایین';
      riskColor = '#4caf50';
    }
  }

  return { text: riskLevel, color: riskColor, risk };
}

// محاسبه RSI
function calculateRSI(prices, period = 14) {
  if (!prices || prices.length < period + 1) return null;

  const rsi = [];
  for (let i = 0; i < prices.length; i++) {
    if (i < period) {
      rsi.push(null);
      continue;
    }

    let gains = 0;
    let losses = 0;

    for (let j = i - period + 1; j <= i; j++) {
      const change = prices[j] - prices[j - 1];
      if (change > 0) gains += change;
      else losses += Math.abs(change);
    }

    const avgGain = gains / period;
    const avgLoss = losses / period;

    if (avgLoss === 0) {
      rsi.push(100);
    } else {
      const rs = avgGain / avgLoss;
      rsi.push(100 - (100 / (1 + rs)));
    }
  }

  return rsi[rsi.length - 1];
}

// محاسبه EMA
function calculateEMAValue(prices, period) {
  if (!prices || prices.length < period) return null;

  const k = 2 / (period + 1);
  let ema = prices.slice(0, period).reduce((a, b) => a + b, 0) / period;

  for (let i = period; i < prices.length; i++) {
    ema = prices[i] * k + ema * (1 - k);
  }

  return ema;
}

// تشخیص روند بر اساس EMA
function getTrendStatus(prices) {
  if (!prices || prices.length < 50) return { text: 'نامشخص', color: '#9e9e9e', level: 0 };

  const ema20 = calculateEMAValue(prices.slice(-20), 20);
  const ema50 = calculateEMAValue(prices.slice(-50), 50);

  if (!ema20 || !ema50) return { text: 'نامشخص', color: '#9e9e9e', level: 0 };

  if (ema20 > ema50 && prices[prices.length - 1] > ema20) {
    return { text: 'صعودی قوی', color: '#4caf50', level: 3 };
  } else if (ema20 > ema50) {
    return { text: 'صعودی', color: '#8bc34a', level: 2 };
  } else if (ema20 < ema50 && prices[prices.length - 1] < ema20) {
    return { text: 'نزولی قوی', color: '#f44336', level: -3 };
  } else if (ema20 < ema50) {
    return { text: 'نزولی', color: '#ff9800', level: -2 };
  }

  return { text: 'خنثی', color: '#ffeb3b', level: 0 };
}

// تابع برای تخمین RSI از قیمت (شبیه‌سازی)
function estimateRSIFromPrice(basePrice) {
  // تخمین بر اساس قیمت - برای زمانی که داده تاریخی نداریم
  // این یک تخمین ساده است و نباید به عنوان RSI واقعی استفاده شود
  const random = Math.sin(basePrice * 9999) * 0.5 + 0.5;
  return 30 + random * 40; // محدوده 30-70
}

// تابع اصلی برای دریافت تحلیل تکنیکال نماد پایه
function getTechnicalAnalysis(baseSymbol, basePrice) {
  // در نسخه فعلی، داده تاریخی در دسترس نیست
  // بنابراین از تخمین استفاده می‌کنیم

  const estimatedRSI = estimateRSIFromPrice(basePrice);

  // تشخیص RSI
  let rsiStatus = { text: 'خرید', color: '#4caf50' };
  if (estimatedRSI > 70) rsiStatus = { text: 'اشباع خرید', color: '#f44336' };
  else if (estimatedRSI > 60) rsiStatus = { text: 'خرید', color: '#8bc34a' };
  else if (estimatedRSI > 40) rsiStatus = { text: 'خنثی', color: '#ffeb3b' };
  else if (estimatedRSI > 30) rsiStatus = { text: 'فروش', color: '#ff9800' };
  else rsiStatus = { text: 'اشباع فروش', color: '#4caf50' };

  return {
    rsi: estimatedRSI,
    rsiStatus,
    trend: getTrendStatus([basePrice]), // برای تخمین روند
    trendStatus: getTrendStatus([basePrice])
  };
}

// =====================================================
// توابع کمکی برای یونانی‌ها (Greeks)
// =====================================================

// تابع برای محاسبه Greeks یک آپشن
function calculateOptionGreeks(option, basePrice) {
  if (!basePrice || !option.price || !option.strike_price) {
    return null;
  }

  const S = basePrice;
  const K = option.strike_price;
  const optionType = option.type === 'call' ? 'call' : 'put';
  const T = calculateTimeToMaturity(option.maturity_date);

  if (T <= 0) return null;

  // نرخ بهره پیش‌فرض 30%
  const r = 0.30;
  const sigma = 0.40; // IV پیش‌فرض

  const greeks = calculateGreeks(S, K, T, r, sigma, optionType);

  if (greeks) {
    return {
      delta: greeks.delta,
      gamma: greeks.gamma,
      vega: greeks.vega,
      theta: greeks.theta,
      rho: greeks.rho
    };
  }

  return null;
}

// تابع برای دریافت وضعیت Delta
function getDeltaStatus(delta, optionType) {
  if (delta === null) return { text: '-', color: '#9e9e9e' };

  if (optionType === 'call') {
    if (delta >= 0.7) return { text: 'بسیار قوی', color: '#4caf50' };
    if (delta >= 0.5) return { text: 'قوی', color: '#8bc34a' };
    if (delta >= 0.3) return { text: 'متوسط', color: '#ffeb3b' };
    if (delta >= 0.1) return { text: 'ضعیف', color: '#ff9800' };
    return { text: 'بسیار ضعیف', color: '#f44336' };
  } else {
    // Put
    if (delta <= -0.7) return { text: 'بسیار قوی', color: '#4caf50' };
    if (delta <= -0.5) return { text: 'قوی', color: '#8bc34a' };
    if (delta <= -0.3) return { text: 'متوسط', color: '#ffeb3b' };
    if (delta <= -0.1) return { text: 'ضعیف', color: '#ff9800' };
    return { text: 'بسیار ضعیف', color: '#f44336' };
  }
}

// تابع برای دریافت وضعیت Theta (افت زمانی)
function getThetaStatus(theta) {
  if (theta === null) return { text: '-', color: '#9e9e9e' };

  // Theta همیشه منفی است برای لانگ پوزیشن
  const absTheta = Math.abs(theta);

  if (absTheta > 50) return { text: 'افت شدید', color: '#f44336' };
  if (absTheta > 30) return { text: 'افت بالا', color: '#ff9800' };
  if (absTheta > 15) return { text: 'افت متوسط', color: '#ffeb3b' };
  if (absTheta > 5) return { text: 'افت کم', color: '#8bc34a' };
  return { text: 'افت ناچیز', color: '#4caf50' };
}

// تابع برای دریافت وضعیت Gamma (سرعت تغییر دلتا)
function getGammaStatus(gamma) {
  if (gamma === null) return { text: '-', color: '#9e9e9e' };

  if (gamma > 0.05) return { text: 'بسیار حساس', color: '#f44336' };
  if (gamma > 0.02) return { text: 'حساس', color: '#ff9800' };
  if (gamma > 0.01) return { text: 'متوسط', color: '#ffeb3b' };
  return { text: 'کم', color: '#4caf50' };
}

// =====================================================
// توابع کمکی برای IV Rank و IV Percentile
// =====================================================

// تابع اصلی برای محاسبه IV Rank و Percentile برای یک نماد پایه
function calculateIVMetrics(options, basePrices) {
  const ivs = [];

  options.forEach(opt => {
    const baseSymbol = opt.base_symbol;
    const basePrice = basePrices[baseSymbol]?.price || 0;
    const strikePrice = opt.strike_price || 0;
    const optionPrice = opt.price || 0;
    const maturityDate = opt.maturity_date;
    const optionType = opt.type === 'call' ? 'call' : 'put';

    if (basePrice > 0 && strikePrice > 0 && optionPrice > 0) {
      const result = calculateOptionValueFromAPI(
        opt.symbol,
        basePrice,
        optionPrice,
        strikePrice,
        maturityDate,
        baseSymbol,
        optionType,
        0,
        0,
        0.40,
        0.30
      );

      if (result.ivSolved && result.ivToday) {
        ivs.push({
          symbol: opt.symbol,
          strikePrice,
          type: optionType,
          iv: parseFloat(result.ivToday),
          timeToMaturity: parseFloat(result.timeToMaturity)
        });
      }
    }
  });

  if (ivs.length === 0) {
    return { ivRank: null, ivPercentile: null, avgIV: null, ivRange: null };
  }

  // مرتب‌سازی IV‌ها
  const sortedIVs = ivs.map(i => i.iv).sort((a, b) => a - b);
  const avgIV = sortedIVs.reduce((a, b) => a + b, 0) / sortedIVs.length;
  const minIV = sortedIVs[0];
  const maxIV = sortedIVs[sortedIVs.length - 1];
  const range = maxIV - minIV;

  // IV Percentile: درصد آپشن‌های با IV کمتر از میانگین
  const belowAvg = sortedIVs.filter(iv => iv <= avgIV).length;
  const ivPercentile = Math.round((belowAvg / sortedIVs.length) * 100);

  // IV Rank: رتبه IV فعلی نسبت به محدوده (تخمین بر اساس ATM)
  // فرض می‌کنیم میانگین = "معمولی" = rank 50
  // اگر IV فعلی بالاتر از میانگین باشد rank > 50
  let ivRank;
  if (range > 0) {
    // استفاده از میانگین به عنوان مرجع
    const deviationFromAvg = (avgIV - minIV) / range;
    ivRank = Math.round(deviationFromAvg * 100);
  } else {
    ivRank = 50; // وقتی همه IV‌ها برابرند
  }

  return {
    ivRank,
    ivPercentile,
    avgIV: (avgIV * 100).toFixed(1),
    minIV: (minIV * 100).toFixed(1),
    maxIV: (maxIV * 100).toFixed(1),
    ivRange: (range * 100).toFixed(1)
  };
}

// تابع برای دریافت IV یک آپشن خاص
function getOptionIV(option, basePrice) {
  if (!basePrice || !option.price || !option.strike_price) return null;

  const optionType = option.type === 'call' ? 'call' : 'put';
  const result = calculateOptionValueFromAPI(
    option.symbol,
    basePrice,
    option.price,
    option.strike_price,
    option.maturity_date,
    option.base_symbol,
    optionType,
    0,
    0,
    0.40,
    0.30
  );

  return result.ivSolved ? parseFloat(result.ivToday) : null;
}

// تابع برای تشخیص وضعیت IV
function getIVStatus(ivRank, ivPercentile) {
  if (ivRank === null) return { text: 'نامشخص', color: '#9e9e9e', level: 0 };

  if (ivRank >= 70) return { text: 'بسیار بالا', color: '#c62828', level: 5 };
  if (ivRank >= 55) return { text: 'بالا', color: '#f44336', level: 4 };
  if (ivRank >= 45) return { text: 'متوسط', color: '#ffeb3b', level: 3 };
  if (ivRank >= 30) return { text: 'پایین', color: '#8bc34a', level: 2 };
  return { text: 'بسیار پایین', color: '#4caf50', level: 1 };
}

function getDaysLeft(jalaliDateStr) {
  if (!jalaliDateStr) return 0;
  const [jy, jm, jd] = jalaliDateStr.split('/').map(Number);
  const epbase = jy - (jy >= 0 ? 474 : 473);
  const epY = 474 + (epbase % 2820);
  const md = (jm - 1) * 31 - Math.floor(jm / 7) * (jm - 7);
  const targetJdn = jd + md + Math.floor((epY * 682 - 110) / 2816) + (epY - 1) * 365 + Math.floor(epbase / 2820) * 1029983 + (1948320 - 1);
  const today = new Date();
  const gy = today.getFullYear(), gm = today.getMonth() + 1, gd = today.getDate();
  const todayJdn = Math.floor((1461 * (gy + 4800 + Math.floor((gm - 14) / 12))) / 4) +
    Math.floor((367 * (gm - 2 - 12 * Math.floor((gm - 14) / 12))) / 12) -
    Math.floor((3 * Math.floor((gy + 4900 + Math.floor((gm - 14) / 12)) / 100)) / 4) + gd - 32075;
  const diff = targetJdn - todayJdn;
  return diff > 0 ? diff : 0;
}

function getLiquidityStatus(volume) {
  if (!volume || volume === 0) return { text: 'صفر', color: '#f44336', level: 0 };
  if (volume < 1000) return { text: 'بسیار کم', color: '#f44336', level: 1 };
  if (volume < 10000) return { text: 'کم', color: '#ff9800', level: 2 };
  if (volume < 50000) return { text: 'متوسط', color: '#ffeb3b', level: 3 };
  if (volume < 100000) return { text: 'خوب', color: '#8bc34a', level: 4 };
  return { text: 'عالی', color: '#4caf50', level: 5 };
}

function getRiskLevel(strategyType, option, S, K) {
  const T_days = getDaysLeft(option.maturity_date);
  let risk = 'متوسط';
  let riskColor = '#ffeb3b';
  if (T_days < 7) { risk = 'بسیار بالا'; riskColor = '#c62828'; }
  else if (T_days < 14) { risk = 'بالا'; riskColor = '#f44336'; }
  else if (T_days > 60) { risk = 'پایین'; riskColor = '#4caf50'; }
  else if (T_days > 30) { risk = 'متوسط'; riskColor = '#ffeb3b'; }
  if (strategyType.includes('Spread') || strategyType.includes('Condor')) {
    if (risk === 'پایین') { risk = 'متوسط'; riskColor = '#ffeb3b'; }
  }
  return { text: risk, color: riskColor };
}

// تابع محاسبه امتیاز استراتژی با در نظر گرفتن IV
function calculateStrategyScore(strategy, option, S, K, volume, T_days, ivRank = null, ivStatus = null) {
  let score = 50;
  const reasons = [];
  const liquidity = getLiquidityStatus(volume);
  score += (liquidity.level - 2) * 5;
  if (liquidity.level >= 4) reasons.push('نقدشوندگی عالی');
  else if (liquidity.level >= 3) reasons.push('نقدشوندگی خوب');
  else if (liquidity.level <= 1) reasons.push('نقدشوندگی پایین - احتیاط');
  if (T_days >= 20 && T_days <= 45) { score += 15; reasons.push('زمان بهینه (20-45 روز)'); }
  else if (T_days >= 15 && T_days < 20) { score += 10; reasons.push('زمان مناسب'); }
  else if (T_days < 7) { score -= 15; reasons.push('زمان کم - ریسک بالا'); }
  else if (T_days > 60) { score += 5; reasons.push('زمان زیاد'); }
  const moneyness = strategy.includes('put') ? (K - S) / S * 100 : (S - K) / S * 100;
  if (strategy === 'buy_call' || strategy === 'buy_put') {
    if (Math.abs(moneyness) <= 3) { score += 20; reasons.push('ATM - بهینه'); }
    else if (Math.abs(moneyness) <= 7) { score += 10; reasons.push('نزدیک ATM'); }
    else if (Math.abs(moneyness) > 15) { score -= 10; reasons.push('OTM زیاد'); }
  } else if (strategy === 'cash_secured_put' || strategy === 'covered_call') {
    if (moneyness > 3 && moneyness < 15) { score += 20; reasons.push('OTM مناسب برای فروش'); }
    else if (moneyness <= 0) { score -= 15; reasons.push('ITM - ریسک زیاد'); }
  }
  const premiumPercent = (option.price || 0) / S * 100;
  if (premiumPercent >= 1 && premiumPercent <= 5) { score += 10; reasons.push('پریمیوم مناسب'); }
  else if (premiumPercent < 1) { score -= 5; reasons.push('پریمیوم کم'); }
  else if (premiumPercent > 8) { score += 5; reasons.push('پریمیوم بالا'); }
  const riskScore = getRiskLevel(strategy, option, S, K);
  if (riskScore.text === 'پایین') score += 10;
  else if (riskScore.text === 'بالا') score -= 10;
  else if (riskScore.text === 'بسیار بالا') score -= 20;

  // === امتیازدهی بر اساس IV Rank ===
  if (ivRank !== null) {
    if (ivRank >= 70) {
      score += 15; // IV بسیار بالا - برای فروش استرس عالی است
      reasons.push('IV بالا - فروش');
    } else if (ivRank >= 55) {
      score += 10;
      reasons.push('IV مناسب');
    } else if (ivRank >= 45) {
      // IV متوسط - نه خوب نه بد
    } else if (ivRank >= 30) {
      score -= 5; // IV پایین
      reasons.push('IV پایین');
    } else {
      score -= 10; // IV بسیار پایین
      reasons.push('IV بسیار پایین');
    }
  }

  return { score: Math.max(0, Math.min(100, score)), reasons, liquidity, risk: riskScore, ivStatus };
}

function calculateProfitLoss(strategy, S, K1, K2, premium, quantity = 1) {
  const multiplier = 1000;
  switch (strategy) {
    case 'buy_call':
      return { maxLoss: premium * multiplier * quantity, maxProfit: 'نامحدود', breakeven: K1 + premium };
    case 'buy_put':
      return { maxLoss: premium * multiplier * quantity, maxProfit: (K1 - premium) * multiplier * quantity, breakeven: K1 - premium };
    case 'bull_call_spread':
    case 'bear_put_spread': {
      const spread = Math.abs(K2 - K1);
      const maxLoss = premium * multiplier * quantity;
      const maxProfit = (spread - premium) * multiplier * quantity;
      return { maxLoss, maxProfit, breakeven: K1 + premium };
    }
    case 'cash_secured_put': {
      const collateral = K1 * multiplier * quantity;
      const maxProfit = premium * multiplier * quantity;
      return { maxLoss: collateral - maxProfit, maxProfit, breakeven: K1 - premium, collateral };
    }
    case 'conversion':
    case 'reversal': {
      // آربیتراژ - سود/ضرر محدود
      // این استراتژی‌ها معمولاً ریسک پایینی دارند
      // سود = ارزش در سررسید - هزینه خالص
      return { maxLoss: '0', maxProfit: 'آربیتراژ', breakeven: K1 };
    }
    default:
      return { maxLoss: 0, maxProfit: 0, breakeven: 0 };
  }
}

const STRATEGIES = [
  { id: 'buy_call', name: 'Buy Call', nameFa: 'خرید Call', description: 'خرید آپشن خرید', direction: 'bullish', minConfidence: 60 },
  { id: 'bull_call_spread', name: 'Bull Call Spread', nameFa: 'Bull Call Spread', description: 'خرید Call + فروش Call', direction: 'bullish', minConfidence: 50 },
  { id: 'cash_secured_put', name: 'Cash Secured Put', nameFa: 'Cash Secured Put', description: 'فروش Put با پوشش نقدی', direction: 'bullish', minConfidence: 45 },
  { id: 'covered_call', name: 'Covered Call', nameFa: 'Covered Call', description: 'فروش Call روی سهام موجود', direction: 'neutral', minConfidence: 50 },
  { id: 'buy_put', name: 'Buy Put', nameFa: 'خرید Put', description: 'خرید آپشن فروش', direction: 'bearish', minConfidence: 60 },
  { id: 'bear_put_spread', name: 'Bear Put Spread', nameFa: 'Bear Put Spread', description: 'خرید Put + فروش Put', direction: 'bearish', minConfidence: 50 },
  { id: 'iron_condor', name: 'Iron Condor', nameFa: 'Iron Condor', description: '4 پایه Put و Call', direction: 'neutral', minConfidence: 40 },
  { id: 'conversion', name: 'Conversion', nameFa: 'Conversion', description: 'آربیتراژ: خرید سهام + خرید Put + فروش Call', direction: 'neutral', minConfidence: 30 },
  { id: 'reversal', name: 'Reversal', nameFa: 'Reversal', description: 'آربیتراژ: فروش سهام + فروش Put + خرید Call', direction: 'neutral', minConfidence: 30 },
];

function OptionStrategies() {
  const [selectedStrategy, setSelectedStrategy] = useState('buy_call');
  const [options, setOptions] = useState([]);
  const [basePrices, setBasePrices] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchBase, setSearchBase] = useState('');

  // تنظیمات فیلتر Conversion/Reversal
  const [minDays, setMinDays] = useState(20);
  const [maxDays, setMaxDays] = useState(35);
  const [minProfit, setMinProfit] = useState(7);

  useEffect(() => {
    fetchOptions();
    const interval = setInterval(() => fetchOptions(false), 30000);
    return () => clearInterval(interval);
  }, []);
  const fetchOptions = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const data = await api.get('/api/options-chain');
      setOptions(data.options || []);
      setBasePrices(data.base_prices || {});
    } catch (err) { console.error('Error:', err); }
    if (showLoading) setLoading(false);
  };

  // محاسبه IV Metrics برای هر نماد پایه
  const ivMetricsByBase = useMemo(() => {
    const metrics = {};
    const baseSymbols = [...new Set(options.map(o => o.base_symbol))];

    baseSymbols.forEach(baseSymbol => {
      const baseOptions = options.filter(o => o.base_symbol === baseSymbol);
      metrics[baseSymbol] = calculateIVMetrics(baseOptions, basePrices);
    });

    return metrics;
  }, [options, basePrices]);

  // محاسبه Greeks برای هر آپشن
  const GreeksByOption = useMemo(() => {
    const greeksMap = {};
    options.forEach(opt => {
      const basePrice = basePrices[opt.base_symbol]?.price || 0;
      if (basePrice > 0) {
        greeksMap[opt.symbol] = calculateOptionGreeks(opt, basePrice);
      }
    });
    return greeksMap;
  }, [options, basePrices]);

  // محاسبه تحلیل تکنیکال برای هر نماد پایه
  const technicalAnalysisByBase = useMemo(() => {
    const techMap = {};
    const baseSymbols = [...new Set(options.map(o => o.base_symbol))];

    baseSymbols.forEach(baseSymbol => {
      const basePrice = basePrices[baseSymbol]?.price || 0;
      if (basePrice > 0) {
        techMap[baseSymbol] = getTechnicalAnalysis(baseSymbol, basePrice);
        // افزودن اطلاعات earnings
        techMap[baseSymbol].earnings = estimateEarningsProximity(baseSymbol);
      }
    });

    return techMap;
  }, [options, basePrices]);

  const strategySignals = useMemo(() => {
    const strategy = STRATEGIES.find(s => s.id === selectedStrategy);
    if (!strategy) return [];
    let filteredOptions = options;
    if (searchBase) {
      filteredOptions = options.filter(opt =>
        (opt.base_symbol || '').toLowerCase().includes(searchBase.toLowerCase()) ||
        (opt.symbol || '').toLowerCase().includes(searchBase.toLowerCase())
      );
    }
    const signals = [];
    const baseSymbols = [...new Set(filteredOptions.map(o => o.base_symbol))];
    baseSymbols.forEach(baseSymbol => {
      const baseOptions = filteredOptions.filter(o => o.base_symbol === baseSymbol);
      const basePriceInfo = basePrices[baseSymbol] || {};
      const S = basePriceInfo?.price || 0;
      const ivMetrics = ivMetricsByBase[baseSymbol] || {};
      const ivStatus = getIVStatus(ivMetrics.ivRank, ivMetrics.ivPercentile);
      const techAnalysis = technicalAnalysisByBase[baseSymbol] || null;

      if (S === 0) return;

      // فیلتر قراردادهای معتبر (حداقل 7 روز تا سررسید و حجم > 0)
      const validCalls = baseOptions.filter(o => o.type === 'call' && getDaysLeft(o.maturity_date) > 7 && (o.volume || 0) > 0);
      const validPuts = baseOptions.filter(o => o.type === 'put' && getDaysLeft(o.maturity_date) > 7 && (o.volume || 0) > 0);

      if (selectedStrategy === 'buy_call') {
        validCalls.forEach(opt => {
          const K = opt.strike_price || 0;
          const T_days = getDaysLeft(opt.maturity_date);
          const greeks = GreeksByOption[opt.symbol] || null;
          const deltaStatus = greeks ? getDeltaStatus(greeks.delta, 'call') : { text: '-', color: '#9e9e9e' };
          const thetaStatus = greeks ? getThetaStatus(greeks.theta) : { text: '-', color: '#9e9e9e' };
          const gammaStatus = greeks ? getGammaStatus(greeks.gamma) : { text: '-', color: '#9e9e9e' };
          const analysis = calculateStrategyScore('buy_call', opt, S, K, opt.volume, T_days, ivMetrics.ivRank, ivStatus);
          const pnl = calculateProfitLoss('buy_call', S, K, 0, opt.price || 0);
          if (analysis.score > 40) {
            signals.push({ symbol: opt.symbol, baseSymbol, basePrice: S, strikePrice: K, premium: opt.price, type: 'Call', expiry: opt.maturity_date, daysToExpiry: T_days, volume: opt.volume, liquidity: analysis.liquidity, risk: analysis.risk, score: analysis.score, reasons: analysis.reasons, ivRank: ivMetrics.ivRank, ivStatus, greeks, deltaStatus, thetaStatus, gammaStatus, techAnalysis, ...pnl });
          }
        });
      } else if (selectedStrategy === 'buy_put') {
        validPuts.forEach(opt => {
          const K = opt.strike_price || 0;
          const T_days = getDaysLeft(opt.maturity_date);
          const greeks = GreeksByOption[opt.symbol] || null;
          const deltaStatus = greeks ? getDeltaStatus(greeks.delta, 'put') : { text: '-', color: '#9e9e9e' };
          const thetaStatus = greeks ? getThetaStatus(greeks.theta) : { text: '-', color: '#9e9e9e' };
          const gammaStatus = greeks ? getGammaStatus(greeks.gamma) : { text: '-', color: '#9e9e9e' };
          const analysis = calculateStrategyScore('buy_put', opt, S, K, opt.volume, T_days, ivMetrics.ivRank, ivStatus);
          const pnl = calculateProfitLoss('buy_put', S, K, 0, opt.price || 0);
          if (analysis.score > 40) {
            signals.push({ symbol: opt.symbol, baseSymbol, basePrice: S, strikePrice: K, premium: opt.price, type: 'Put', expiry: opt.maturity_date, daysToExpiry: T_days, volume: opt.volume, liquidity: analysis.liquidity, risk: analysis.risk, score: analysis.score, reasons: analysis.reasons, ivRank: ivMetrics.ivRank, ivStatus, greeks, deltaStatus, thetaStatus, gammaStatus, techAnalysis, ...pnl });
          }
        });
      } else if (selectedStrategy === 'cash_secured_put') {
        validPuts.forEach(opt => {
          const K = opt.strike_price || 0;
          const T_days = getDaysLeft(opt.maturity_date);
          const greeks = GreeksByOption[opt.symbol] || null;
          const deltaStatus = greeks ? getDeltaStatus(greeks.delta, 'put') : { text: '-', color: '#9e9e9e' };
          const thetaStatus = greeks ? getThetaStatus(greeks.theta) : { text: '-', color: '#9e9e9e' };
          const gammaStatus = greeks ? getGammaStatus(greeks.gamma) : { text: '-', color: '#9e9e9e' };
          const analysis = calculateStrategyScore('cash_secured_put', opt, S, K, opt.volume, T_days, ivMetrics.ivRank, ivStatus);
          const pnl = calculateProfitLoss('cash_secured_put', S, K, 0, opt.price || 0);
          if (analysis.score > 40) {
            signals.push({ symbol: opt.symbol, baseSymbol, basePrice: S, strikePrice: K, premium: opt.price, type: 'Put', expiry: opt.maturity_date, daysToExpiry: T_days, volume: opt.volume, liquidity: analysis.liquidity, risk: analysis.risk, score: analysis.score, reasons: analysis.reasons, premiumPercent: ((opt.price || 0) / S * 100).toFixed(1) + '%', collateral: (K * 1000).toLocaleString(), ivRank: ivMetrics.ivRank, ivStatus, greeks, deltaStatus, thetaStatus, gammaStatus, techAnalysis, ...pnl });
          }
        });
      } else if (selectedStrategy === 'covered_call') {
        validCalls.forEach(opt => {
          const K = opt.strike_price || 0;
          const T_days = getDaysLeft(opt.maturity_date);
          const greeks = GreeksByOption[opt.symbol] || null;
          const deltaStatus = greeks ? getDeltaStatus(greeks.delta, 'call') : { text: '-', color: '#9e9e9e' };
          const thetaStatus = greeks ? getThetaStatus(greeks.theta) : { text: '-', color: '#9e9e9e' };
          const gammaStatus = greeks ? getGammaStatus(greeks.gamma) : { text: '-', color: '#9e9e9e' };
          const moneyness = (K - S) / S * 100;
          const analysis = calculateStrategyScore('covered_call', opt, S, K, opt.volume, T_days, ivMetrics.ivRank, ivStatus);
          if (moneyness > 0 && moneyness < 20 && analysis.score > 40) {
            signals.push({ symbol: opt.symbol, baseSymbol, basePrice: S, strikePrice: K, premium: opt.price, type: 'Call', expiry: opt.maturity_date, daysToExpiry: T_days, volume: opt.volume, liquidity: analysis.liquidity, risk: analysis.risk, score: analysis.score, reasons: analysis.reasons, moneyness: '+' + moneyness.toFixed(1) + '%', ivRank: ivMetrics.ivRank, ivStatus, greeks, deltaStatus, thetaStatus, gammaStatus, techAnalysis });
          }
        });
      } else if (selectedStrategy === 'bull_call_spread') {
        const spreadCalls = validCalls.sort((a, b) => (a.strike_price || 0) - (b.strike_price || 0));
        for (let i = 0; i < spreadCalls.length - 1; i++) {
          const longCall = spreadCalls[i], shortCall = spreadCalls[i + 1];
          const K_long = longCall.strike_price || 0, K_short = shortCall.strike_price || 0;
          const netPremium = (longCall.price || 0) - (shortCall.price || 0);
          const T_days = getDaysLeft(longCall.maturity_date);
          const totalVolume = (longCall.volume || 0) + (shortCall.volume || 0);
          if (netPremium > 0) {
            const pnl = calculateProfitLoss('bull_call_spread', S, K_long, K_short, netPremium);
            const analysis = calculateStrategyScore('bull_call_spread', longCall, S, K_long, totalVolume, T_days, ivMetrics.ivRank, ivStatus);
            if (analysis.score > 45) {
              signals.push({ symbol: `${longCall.symbol}+${shortCall.symbol}`, baseSymbol, basePrice: S, strikePrice: `${K_long}/${K_short}`, premium: netPremium.toFixed(0), type: 'Call Spread', expiry: longCall.maturity_date, daysToExpiry: T_days, volume: totalVolume, liquidity: analysis.liquidity, risk: analysis.risk, score: analysis.score, reasons: analysis.reasons, spread: ((K_short - K_long) / S * 100).toFixed(1) + '%', ivRank: ivMetrics.ivRank, ivStatus, deltaStatus: { text: 'ترکیبی', color: '#9e9e9e' }, thetaStatus: { text: 'ترکیبی', color: '#9e9e9e' }, gammaStatus: { text: 'ترکیبی', color: '#9e9e9e' }, ...pnl });
            }
          }
        }
      } else if (selectedStrategy === 'bear_put_spread') {
        const spreadPuts = validPuts.sort((a, b) => (b.strike_price || 0) - (a.strike_price || 0));
        for (let i = 0; i < spreadPuts.length - 1; i++) {
          const longPut = spreadPuts[i], shortPut = spreadPuts[i + 1];
          const K_long = longPut.strike_price || 0, K_short = shortPut.strike_price || 0;
          const netPremium = (longPut.price || 0) - (shortPut.price || 0);
          const T_days = getDaysLeft(longPut.maturity_date);
          const totalVolume = (longPut.volume || 0) + (shortPut.volume || 0);
          if (netPremium > 0) {
            const pnl = calculateProfitLoss('bear_put_spread', S, K_long, K_short, netPremium);
            const analysis = calculateStrategyScore('bear_put_spread', longPut, S, K_long, totalVolume, T_days, ivMetrics.ivRank, ivStatus);
            if (analysis.score > 45) {
              signals.push({ symbol: `${longPut.symbol}+${shortPut.symbol}`, baseSymbol, basePrice: S, strikePrice: `${K_long}/${K_short}`, premium: netPremium.toFixed(0), type: 'Put Spread', expiry: longPut.maturity_date, daysToExpiry: T_days, volume: totalVolume, liquidity: analysis.liquidity, risk: analysis.risk, score: analysis.score, reasons: analysis.reasons, ivRank: ivMetrics.ivRank, ivStatus, deltaStatus: { text: 'ترکیبی', color: '#9e9e9e' }, thetaStatus: { text: 'ترکیبی', color: '#9e9e9e' }, gammaStatus: { text: 'ترکیبی', color: '#9e9e9e' }, ...pnl });
            }
          }
        }
      } else if (selectedStrategy === 'iron_condor') {
        if (validPuts.length >= 2 && validCalls.length >= 2) {
          // مرتب کردن برای پیدا کردن قیمت‌های مناسب
          const sortedPuts = validPuts.sort((a, b) => (b.strike_price || 0) - (a.strike_price || 0));
          const sortedCalls = validCalls.sort((a, b) => (a.strike_price || 0) - (b.strike_price || 0));

          // انتخاب 4 پایه برای Iron Condor
          // Put با قیمت پایین‌تر (protected Put)
          const longPut = sortedPuts[sortedPuts.length - 1];
          // Put با قیمت بالاتر (sold Put)
          const shortPut = sortedPuts[Math.floor(sortedPuts.length / 2)];
          // Call با قیمت پایین‌تر (sold Call)
          const shortCall = sortedCalls[Math.floor(sortedCalls.length / 2)];
          // Call با قیمت بالاتر (protected Call)
          const longCall = sortedCalls[sortedCalls.length - 1];

          const T_days = getDaysLeft(shortPut.maturity_date);
          const totalVolume = (longPut.volume || 0) + (shortPut.volume || 0) + (shortCall.volume || 0) + (longCall.volume || 0);
          const liquidity = getLiquidityStatus(totalVolume);

          // نمایش 4 نماد
          const icSymbol = `IC: ${longPut.symbol} + ${shortPut.symbol} + ${shortCall.symbol} + ${longCall.symbol}`;
          const strikePrices = `${longPut.strike_price}/${shortPut.strike_price} / ${shortCall.strike_price}/${longCall.strike_price}`;

          // محاسبه پریمیوم خالص (درآمد فروش - هزینه خرید)
          const totalPremium = ((shortPut.price || 0) + (shortCall.price || 0)) - ((longPut.price || 0) + (longCall.price || 0));

          signals.push({
            symbol: icSymbol,
            baseSymbol,
            basePrice: S,
            strikePrice: strikePrices,
            premium: totalPremium > 0 ? `+${totalPremium.toFixed(0)}` : totalPremium.toFixed(0),
            type: 'Iron Condor',
            expiry: shortPut.maturity_date,
            daysToExpiry: T_days,
            volume: totalVolume,
            liquidity,
            risk: { text: 'پایین', color: '#4caf50' },
            score: 50 + (liquidity.level - 2) * 5,
            reasons: ['استراتژی ریسک پایین - ۴ پایه'],
            ivRank: ivMetrics.ivRank,
            ivStatus,
            deltaStatus: { text: 'ترکیبی', color: '#9e9e9e' },
            thetaStatus: { text: 'ترکیبی', color: '#9e9e9e' },
            gammaStatus: { text: 'ترکیبی', color: '#9e9e9e' },
            techAnalysis,
            legs: {
              longPut: longPut.symbol,
              shortPut: shortPut.symbol,
              shortCall: shortCall.symbol,
              longCall: longCall.symbol
            }
          });
        }
      } else if (selectedStrategy === 'conversion' || selectedStrategy === 'reversal') {
        // استراتژی Conversion و Reversal (آربیتراژ)
        // Conversion: Long Stock + Long Put + Short Call
        // Reversal: Short Stock + Short Put + Long Call
        // هر دو با یک قیمت اعمال

        const isConversion = selectedStrategy === 'conversion';

        // برای Conversion: بهترین حالت ATM یا کمی ITM برای Put
        // برای Reversal: بهترین حالت ATM یا کمی ITM برای Call

        // مرتب کردن بر اساس نزدیکی به قیمت پایه
        const allOptions = [...validCalls, ...validPuts].sort((a, b) =>
          Math.abs((a.strike_price || 0) - S) - Math.abs((b.strike_price || 0) - S)
        );

        // فقط از strike هایی استفاده کنیم که هم call هم put دارند
        const uniqueStrikes = [...new Set(allOptions.map(o => o.strike_price))].sort((a, b) => a - b);

        uniqueStrikes.forEach(strike => {
          const callOption = validCalls.find(o => o.strike_price === strike);
          const putOption = validPuts.find(o => o.strike_price === strike);

          if (!callOption || !putOption) return;

          const T_days = getDaysLeft(callOption.maturity_date);
          const totalVolume = (callOption.volume || 0) + (putOption.volume || 0);
          const liquidity = getLiquidityStatus(totalVolume);

          // محاسبه آربیتراژ
          // Conversion: خرید سهام + خرید Put + فروش Call
          // هزینه خالص = قیمت سهام + پریمیوم Put - پریمیوم Call
          const stockCost = S; // هزینه خرید سهام
          const putPremium = putOption.price || 0; // هزینه خرید Put
          const callPremium = callOption.price || 0; // درآمد فروش Call

          let netCost, arbitrageProfit;

          if (isConversion) {
            // Conversion: Long Stock + Long Put + Short Call
            // در زمان ورود:
            // - خرید سهام: پرداخت S
            // - خرید Put: پرداخت putPremium
            // - فروش Call: دریافت callPremium
            // خالص پرداخت: S + putPremium - callPremium
            netCost = stockCost + putPremium - callPremium;

            // در زمان سررسید (فرض می‌کنیم قیمت سهام = strike):
            // - سهام را به قیمت strike می‌فروشیم: دریافت strike
            // - Put بی‌ارزش (چون S = K)
            // - Call واگذار شده (بی‌ارزش برای ما)
            // خالص دریافتی: strike
            // سود = دریافتی - پرداختی = strike - netCost
            arbitrageProfit = strike - netCost;
          } else {
            // Reversal: Short Stock + Short Put + Long Call
            // در زمان ورود:
            // - فروش سهام: دریافت S
            // - فروش Put: دریافت putPremium
            // - خرید Call: پرداخت callPremium
            // خالص دریافتی: S + putPremium - callPremium = netCost (مثبت)
            netCost = stockCost + putPremium - callPremium;

            // در زمان سررسید:
            // - سهام را به قیمت strike می‌خریم: پرداخت strike
            // - Put بی‌ارزش (چون S = K)
            // - Call بی‌ارزش
            // خالص پرداختی: strike
            // سود = دریافتی - پرداختی = netCost - strike
            arbitrageProfit = netCost - strike;
          }

          // moneyness برای این استراتژی
          const moneyness = (strike - S) / S * 100;

          // امتیازدهی
          let score = 50;
          const reasons = [];

          // نقدشوندگی
          score += (liquidity.level - 2) * 5;
          if (liquidity.level >= 4) reasons.push('نقدشوندگی عالی');
          else if (liquidity.level <= 1) reasons.push('نقدشوندگی پایین');

          // زمان
          if (T_days >= 20 && T_days <= 45) { score += 15; reasons.push('زمان بهینه'); }
          else if (T_days < 7) { score -= 15; reasons.push('زمان کم - ریسک بالا'); }

          // moneyness - برای Conversion بهترین حالت ATM یا کمی ITM
          if (Math.abs(moneyness) <= 3) { score += 20; reasons.push('ATM - بهینه'); }
          else if (Math.abs(moneyness) <= 7) { score += 10; reasons.push('نزدیک ATM'); }
          else if (Math.abs(moneyness) > 15) { score -= 10; reasons.push('OTM زیاد'); }

          // تفاوت پریمیوم‌ها - برای آربیتراژ مهم است
          const premiumDiff = Math.abs(callPremium - putPremium);
          if (premiumDiff < S * 0.02) { score += 10; reasons.push('پریمیوم متعادل'); }
          else if (premiumDiff > S * 0.05) { score -= 5; reasons.push('اختلاف پریمیوم زیاد'); }

          // ریسک - آربیتراژ معمولاً ریسک پایین
          const riskScore = getRiskLevel('conversion', callOption, S, strike);
          if (riskScore.text === 'پایین') score += 10;
          else if (riskScore.text === 'بالا') score -= 10;

          // سود آربیتراژ به صورت درصد
          const arbitragePercent = (arbitrageProfit / stockCost) * 100;

          // === فیلترهای استراتژی Conversion/Reversal ===
          // فیلتر 1: سررسید بین minDays تا maxDays روز
          if (T_days < minDays || T_days > maxDays) return;
          // فیلتر 2: سود آربیتراژ باید حداقل minProfit باشد
          if (arbitragePercent < minProfit) return;

          const convSymbol = isConversion
            ? `Conv: ${baseSymbol} + ${putOption.symbol} + ${callOption.symbol}`
            : `Rev: ${baseSymbol} + ${putOption.symbol} + ${callOption.symbol}`;

          const legSymbol = isConversion
            ? `سهام:${baseSymbol} + Put:${putOption.symbol} + Call:${callOption.symbol}`
            : `سهام:${baseSymbol} + Put:${putOption.symbol} + Call:${callOption.symbol}`;

          signals.push({
            symbol: legSymbol,
            baseSymbol,
            basePrice: S,
            strikePrice: strike,
            premium: `${putPremium.toFixed(0)} - ${callPremium.toFixed(0)}`,
            type: isConversion ? 'Conversion' : 'Reversal',
            expiry: callOption.maturity_date,
            daysToExpiry: T_days,
            volume: totalVolume,
            liquidity,
            risk: { text: 'پایین', color: '#4caf50' },
            score: Math.max(0, Math.min(100, score)),
            reasons,
            moneyness: moneyness.toFixed(1) + '%',
            ivRank: ivMetrics.ivRank,
            ivStatus,
            arbitrageProfit: arbitrageProfit.toFixed(0),
            arbitragePercent: arbitragePercent.toFixed(2) + '%',
            deltaStatus: { text: 'آربیتراژ', color: '#9e9e9e' },
            thetaStatus: { text: 'آربیتراژ', color: '#9e9e9e' },
            gammaStatus: { text: 'آربیتراژ', color: '#9e9e9e' },
            techAnalysis,
            legs: {
              stock: baseSymbol,
              put: putOption.symbol,
              call: callOption.symbol,
              strike: strike
            },
            // برای نمایش سود/ضرر
            maxProfit: arbitrageProfit > 0 ? arbitrageProfit.toLocaleString() : 0,
            maxLoss: '0',
            breakeven: strike.toLocaleString()
          });
        });
      }
    });
    return signals.sort((a, b) => b.score - a.score).slice(0, 50);
  }, [selectedStrategy, options, basePrices, searchBase, ivMetricsByBase, GreeksByOption, technicalAnalysisByBase, minDays, maxDays, minProfit]);

  const selectedStrategyInfo = STRATEGIES.find(s => s.id === selectedStrategy);
  const getSignalBadge = (score) => {
    if (score >= 75) return { text: 'خرید قوی', color: '#4caf50' };
    if (score >= 60) return { text: 'خرید', color: '#8bc34a' };
    if (score >= 45) return { text: 'صبر', color: '#ffeb3b' };
    if (score >= 30) return { text: 'فروش', color: '#ff9800' };
    return { text: 'فروش قوی', color: '#f44336' };
  };

  return (
    <div className="option-strategies">
      <h1>استراتژی های آپشن</h1>
      <div className="training-button"><button onClick={() => window.open('/option-strategies-guide.html', '_blank')}>📖 آموزش</button></div>
      <div className="strategy-selector">
        <label>انتخاب استراتژی:</label>
        <select value={selectedStrategy} onChange={(e) => setSelectedStrategy(e.target.value)}>
          {STRATEGIES.map(s => <option key={s.id} value={s.id}>{s.nameFa} ({s.name})</option>)}
        </select>
      </div>
      {selectedStrategyInfo && (
        <div className="strategy-info">
          <h3>{selectedStrategyInfo.nameFa}</h3>
          <p>{selectedStrategyInfo.description}</p>
          <span className={`direction-badge ${selectedStrategyInfo.direction}`}>
            {selectedStrategyInfo.direction === 'bullish' ? 'صعودی' : selectedStrategyInfo.direction === 'bearish' ? 'نزولی' : 'خنثی'}
          </span>
        </div>
      )}
      <div className="search-box">
        <input type="text" placeholder="جستجوی نماد پایه..." value={searchBase} onChange={(e) => setSearchBase(e.target.value)} />
      </div>
      {/* فیلترهای Conversion/Reversal */}
      {(selectedStrategy === 'conversion' || selectedStrategy === 'reversal') && (
        <div className="arbitrage-filters">
          <div className="filter-group">
            <label>حداقل روز:</label>
            <input
              type="number"
              value={minDays}
              onChange={(e) => setMinDays(Number(e.target.value))}
              min={1}
              max={90}
            />
          </div>
          <div className="filter-group">
            <label>حداکثر روز:</label>
            <input
              type="number"
              value={maxDays}
              onChange={(e) => setMaxDays(Number(e.target.value))}
              min={1}
              max={90}
            />
          </div>
          <div className="filter-group">
            <label>حداقل سود (%):</label>
            <input
              type="number"
              value={minProfit}
              onChange={(e) => setMinProfit(Number(e.target.value))}
              min={0}
              max={100}
            />
          </div>
        </div>
      )}
      {loading ? <div className="loading">در حال بارگذاری...</div> : (
        <div className="signals-table-container">
          <table className="signals-table">
            <thead>
              <tr>
                <th>نماد</th><th>پایه</th><th>قیمت</th><th>اعمال</th><th>پریمیوم</th><th>روز</th><th>حجم</th><th>موقعیت</th><th>نقدشوندگی</th><th>ریسک</th><th>IV Rank</th><th>سود آربیتراژ</th><th>امتیاز</th><th>سیگنال</th>
              </tr>
            </thead>
            <tbody>
              {strategySignals.map((signal, idx) => {
                const badge = getSignalBadge(signal.score);
                // رنگ موقعیت بر اساس مقدار
                const moneynessValue = parseFloat(signal.moneyness?.replace('%', '') || '0');
                const moneynessColor = moneynessValue > 0 ? '#4caf50' : moneynessValue < 0 ? '#f44336' : '#ffeb3b';
                return (
                  <tr key={idx}>
                    <td className="symbol-cell">
                      <span className="option-symbol">{signal.symbol}</span>
                      <span className="option-type">{signal.type}</span>
                    </td>
                    <td>{signal.baseSymbol}</td>
                    <td className="price-cell">{signal.basePrice.toLocaleString()}</td>
                    <td className="strike-cell">{signal.strikePrice.toLocaleString()}</td>
                    <td>{typeof signal.premium === 'number' ? signal.premium.toLocaleString() : signal.premium}</td>
                    <td>{signal.daysToExpiry}</td>
                    <td>{signal.volume ? signal.volume.toLocaleString() : '-'}</td>
                    <td>
                      {signal.moneyness && (
                        <span style={{
                          color: moneynessColor,
                          fontWeight: 'bold',
                          fontSize: '0.85em'
                        }}>
                          {signal.moneyness}
                        </span>
                      )}
                    </td>
                    <td><span className="liquidity-badge" style={{ backgroundColor: signal.liquidity.color }}>{signal.liquidity.text}</span></td>
                    <td><span className="risk-badge" style={{ color: signal.risk.color }}>{signal.risk.text}</span></td>
                    <td><span className="iv-badge" style={{ backgroundColor: signal.ivStatus?.color || '#9e9e9e' }}>{signal.ivRank !== null ? signal.ivRank + '%' : '-'}</span></td>
                    <td>
                      {signal.arbitrageProfit && (
                        <div className="arbitrage-cell">
                          <span style={{ color: parseFloat(signal.arbitrageProfit) >= 0 ? '#4caf50' : '#f44336' }}>
                            {parseFloat(signal.arbitrageProfit).toLocaleString()}
                          </span>
                          {signal.arbitragePercent && (
                            <span style={{
                              color: parseFloat(signal.arbitragePercent) >= 0 ? '#4caf50' : '#f44336',
                              fontSize: '0.75em',
                              marginRight: '4px'
                            }}>
                              ({signal.arbitragePercent})
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td><span className={`score ${signal.score >= 70 ? 'high' : signal.score >= 50 ? 'medium' : 'low'}`}>{signal.score}</span></td>
                    <td><span style={{ color: badge.color, fontWeight: 'bold' }}>{badge.text}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {strategySignals.length === 0 && <div className="no-data"><p>سیگنالی یافت نشد</p></div>}
        </div>
      )}
      <div className="strategy-explanation">
        <h4>راهنمای امتیازدهی:</h4>
        <div className="explanation-grid">
          <div className="explanation-item"><span>نقدشوندگی (25%)</span></div>
          <div className="explanation-item"><span>زمان (20%)</span></div>
          <div className="explanation-item"><span>موقعیت (25%)</span></div>
          <div className="explanation-item"><span>پریمیوم (15%)</span></div>
          <div className="explanation-item"><span>ریسک (15%)</span></div>
        </div>
      </div>
    </div>
  );
}

export default OptionStrategies;
